"use client";

/**
 * IdeMock — animated mock of the askFinz Code IDE for /apps/code marketing.
 *
 * Pure presentation: file tree on the left, editor pane in the middle,
 * agent panel on the right. A scripted "agent run" plays when the mock
 * scrolls into view: a file gets opened, a diff streams in line by line,
 * test count flips green, status pill ticks through.
 *
 * NOT the real IDE (`code.askfinz.ai/components/ide/*`). Lives in
 * askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Folder,
  FolderOpen,
  FileText,
  Play,
  CheckCircle2,
  Bot,
  GitCommit,
  Terminal as TerminalIcon,
  Sparkles,
} from "lucide-react";

type FileNode =
  | { kind: "dir"; name: string; open?: boolean; children: FileNode[] }
  | { kind: "file"; name: string; ext: "ts" | "tsx" | "py" | "md"; active?: boolean };

const TREE: FileNode[] = [
  {
    kind: "dir",
    name: "src",
    open: true,
    children: [
      {
        kind: "dir",
        name: "reports",
        open: true,
        children: [
          { kind: "file", name: "report.builder.ts", ext: "ts", active: true },
          { kind: "file", name: "scoped-client.ts", ext: "ts" },
        ],
      },
      {
        kind: "dir",
        name: "routes",
        open: false,
        children: [
          { kind: "file", name: "list.ts", ext: "ts" },
          { kind: "file", name: "show.ts", ext: "ts" },
        ],
      },
    ],
  },
  {
    kind: "dir",
    name: "tests",
    open: false,
    children: [{ kind: "file", name: "report.spec.ts", ext: "ts" }],
  },
  { kind: "file", name: "README.md", ext: "md" },
];

// The diff streams in line by line — `op` controls the gutter glyph + colour.
type DiffLine = {
  op: " " | "-" | "+";
  text: string;
};

// Fictional refactor — does NOT mirror any real askFinz code. Generic
// names only (no tech-stack hints like JWT, OAuth, Vault, etc.).
const DIFF: DiffLine[] = [
  { op: " ", text: "import { LegacyClient } from './legacy-client';" },
  { op: " ", text: "import { ScopedClient } from './scoped-client';" },
  { op: " ", text: "" },
  { op: " ", text: "export class ReportBuilder {" },
  { op: "-", text: "  private legacy = new LegacyClient({" },
  { op: "-", text: "    endpoint: process.env.REPORT_API!," },
  { op: "-", text: "    timeout: 5000," },
  { op: "-", text: "  });" },
  { op: "+", text: "  private client = new ScopedClient({" },
  { op: "+", text: "    endpoint: process.env.REPORT_API!," },
  { op: "+", text: "    timeout: 8000," },
  { op: "+", text: "    cache: 'short'," },
  { op: "+", text: "  });" },
  { op: " ", text: "" },
  { op: " ", text: "  async build(seed: ReportSeed) {" },
  { op: "-", text: "    return this.legacy.run({ id: seed.id });" },
  { op: "+", text: "    return this.client.compose({ seedId: seed.id });" },
  { op: " ", text: "  }" },
  { op: " ", text: "}" },
];

type AgentStep = {
  label: string;
  status: "queued" | "running" | "done";
  detail?: string;
};

const STEPS_INITIAL: AgentStep[] = [
  // Partial-fill: read + plan already completed before the script kicks in, so
  // the right rail isn't all empty circles on first paint.
  { label: "Read report.builder.ts", status: "done" },
  { label: "Plan refactor (4 steps)", status: "done" },
  { label: "Apply diff", status: "queued" },
  { label: "Run tests", status: "queued", detail: "0 / 14 cases" },
  { label: "Commit", status: "queued" },
];

// First-paint snapshot: show the first 4 context lines of the diff (the
// imports + class signature, no − or + ops yet) so the editor isn't an empty
// pane. The script picks up from here and streams the actual changed lines.
const INITIAL_DIFF_STEP = 4;

export function IdeMock() {
  const [diffStep, setDiffStep] = useState(INITIAL_DIFF_STEP);
  const [steps, setSteps] = useState<AgentStep[]>(STEPS_INITIAL);
  const [status, setStatus] = useState<"idle" | "running" | "complete">("idle");
  const [terminalLines, setTerminalLines] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        setStatus("running");

        const setStepStatus = (i: number, st: AgentStep["status"], detail?: string) => {
          setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, status: st, detail: detail ?? s.detail } : s)));
        };

        // Steps 0 (Read) + 1 (Plan) are already "done" in the initial state.

        // Step 2: Apply diff (stream lines) — pick up from INITIAL_DIFF_STEP so
        // we don't replay the context lines already on screen.
        setTimeout(() => setStepStatus(2, "running"), 200);
        DIFF.slice(INITIAL_DIFF_STEP).forEach((_, i) => {
          setTimeout(() => setDiffStep(INITIAL_DIFF_STEP + i + 1), 300 + i * 110);
        });
        setTimeout(() => setStepStatus(2, "done"), 300 + (DIFF.length - INITIAL_DIFF_STEP) * 110 + 200);

        // Step 3: Run tests (count up)
        const testStart = 300 + (DIFF.length - INITIAL_DIFF_STEP) * 110 + 300;
        setTimeout(() => setStepStatus(3, "running", "0 / 14 cases"), testStart);
        for (let n = 1; n <= 14; n++) {
          setTimeout(() => setStepStatus(3, "running", `${n} / 14 cases`), testStart + n * 90);
        }
        setTimeout(() => setStepStatus(3, "done", "14 / 14 ✓"), testStart + 14 * 90 + 200);

        // Step 4: Commit + terminal log
        const commitStart = testStart + 14 * 90 + 400;
        setTimeout(() => setStepStatus(4, "running"), commitStart);
        setTimeout(() => setTerminalLines((l) => [...l, "$ git commit -m 'reports: ScopedClient migration'"]), commitStart + 200);
        setTimeout(() => setTerminalLines((l) => [...l, "[main 3a9f12c] reports: ScopedClient migration"]), commitStart + 700);
        setTimeout(() => setTerminalLines((l) => [...l, " 7 files changed, 312 insertions(+), 187 deletions(-)"]), commitStart + 900);
        setTimeout(() => {
          setStepStatus(4, "done");
          setStatus("complete");
        }, commitStart + 1100);

        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            code.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (status === "running"
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : status === "complete"
                  ? "bg-foreground/70"
                  : "bg-foreground/20")
              }
            />
            {status === "running" ? "Agent run" : status === "complete" ? "Complete" : "Idle"}
          </span>
        </div>

        <div className="grid grid-cols-12 min-h-[420px] md:min-h-[480px]">
          {/* File tree */}
          <div className="col-span-3 hidden md:flex flex-col border-r hairline bg-foreground/[0.02] p-2 text-[12px]">
            <div className="px-2 pb-2 mb-1 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              Explorer
            </div>
            <Tree nodes={TREE} indent={0} />
          </div>

          {/* Editor */}
          <div className="col-span-12 md:col-span-6 flex flex-col">
            {/* Tab strip */}
            <div className="flex items-center border-b hairline text-[12px] font-mono">
              <div className="px-3 h-9 inline-flex items-center gap-2 border-r hairline bg-background">
                <FileText className="h-3 w-3 text-foreground/65" />
                <span>report.builder.ts</span>
              </div>
              <div className="px-3 h-9 inline-flex items-center gap-2 text-foreground/65">
                <FileText className="h-3 w-3" />
                <span>scoped-client.ts</span>
              </div>
            </div>

            {/* Code body */}
            <div className="flex-1 overflow-hidden font-mono text-[12px] leading-[1.55] py-2 bg-background">
              {DIFF.slice(0, diffStep).map((line, i) => {
                const cls =
                  line.op === "+"
                    ? "bg-[hsl(var(--accent))]/8 text-foreground/90"
                    : line.op === "-"
                    ? "bg-foreground/8 text-foreground/65 line-through decoration-foreground/30"
                    : "text-foreground/75";
                const gutter =
                  line.op === "+"
                    ? "text-[hsl(var(--accent))]"
                    : line.op === "-"
                    ? "text-foreground/65"
                    : "text-foreground/30";
                return (
                  <div key={i} className={"flex animate-[blur-fade-in_0.2s_ease-out] " + cls}>
                    <span className={"w-8 text-right pr-3 select-none " + gutter}>{line.op === " " ? i + 1 : line.op}</span>
                    <span className="whitespace-pre">{line.text || " "}</span>
                  </div>
                );
              })}
              {/* Cursor */}
              {diffStep < DIFF.length && (
                <div className="flex animate-pulse">
                  <span className="w-8" />
                  <span className="inline-block h-3.5 w-1.5 bg-[hsl(var(--accent))]/70 rounded-sm align-middle" />
                </div>
              )}
            </div>

            {/* Terminal strip */}
            <div className="border-t hairline bg-foreground/[0.02] px-3 py-2 font-mono text-[11px] min-h-[56px] max-h-24 overflow-hidden">
              <div className="flex items-center gap-1.5 text-foreground/65 mb-1">
                <TerminalIcon className="h-3 w-3" />
                <span className="uppercase tracking-[0.16em] text-[10px]">Terminal</span>
              </div>
              {terminalLines.map((l, i) => (
                <div key={i} className="text-foreground/75 animate-[blur-fade-in_0.3s_ease-out]">
                  {l}
                </div>
              ))}
            </div>
          </div>

          {/* Agent panel */}
          <div className="col-span-12 md:col-span-3 flex flex-col border-t md:border-t-0 md:border-l hairline bg-foreground/[0.02]">
            <div className="px-3 py-2 border-b hairline flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Bot className="h-3 w-3" />
              <span>Agent · Coder</span>
              <Sparkles className="h-3 w-3 ml-auto text-foreground/65" />
            </div>
            <div className="flex-1 p-3 space-y-2 text-[12px]">
              {steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0">
                    {s.status === "done" ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                    ) : s.status === "running" ? (
                      <Play className="h-3.5 w-3.5 text-foreground/70 animate-pulse" />
                    ) : (
                      <span className="block h-3.5 w-3.5 rounded-full border hairline-strong" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={s.status === "queued" ? "text-foreground/65" : "text-foreground/85"}>
                      {s.label}
                    </div>
                    {s.detail && (
                      <div className="text-[10px] font-mono text-foreground/65 mt-0.5">{s.detail}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {status === "complete" && (
              <div className="px-3 py-2 border-t hairline text-[10px] font-mono text-foreground/65 inline-flex items-center gap-1.5 animate-[blur-fade-in_0.5s_ease-out]">
                <GitCommit className="h-3 w-3" />
                3a9f12c · main
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Tree({ nodes, indent }: { nodes: FileNode[]; indent: number }) {
  return (
    <div>
      {nodes.map((n, i) => {
        const pad = { paddingLeft: 6 + indent * 12 };
        if (n.kind === "dir") {
          return (
            <div key={`d-${i}`}>
              <div
                className="flex items-center gap-1.5 py-0.5 rounded hover:bg-foreground/5 text-foreground/75"
                style={pad}
              >
                {n.open ? (
                  <FolderOpen className="h-3 w-3 text-foreground/65" />
                ) : (
                  <Folder className="h-3 w-3 text-foreground/65" />
                )}
                <span>{n.name}</span>
              </div>
              {n.open && <Tree nodes={n.children} indent={indent + 1} />}
            </div>
          );
        }
        return (
          <div
            key={`f-${i}`}
            className={
              "flex items-center gap-1.5 py-0.5 rounded " +
              (n.active
                ? "bg-foreground text-background"
                : "hover:bg-foreground/5 text-foreground/65")
            }
            style={pad}
          >
            <FileText className="h-3 w-3 opacity-65" />
            <span>{n.name}</span>
          </div>
        );
      })}
    </div>
  );
}
